import { jwtDecode } from 'jwt-decode';

export const getToken = () => localStorage.getItem('token');
export const setToken = (token) => localStorage.setItem('token', token);
export const removeToken = () => localStorage.removeItem('token');

export const getApiBaseUrl = () => {
    const envUrl = (process.env.REACT_APP_API_BASE_URL || '').trim();
    const fallbackUrl = 'http://localhost:3005';
    const baseUrl = envUrl || fallbackUrl;

    // Avoid silently using localhost in production deployments
    if (!envUrl && process.env.NODE_ENV === 'production') {
        // eslint-disable-next-line no-console
        console.warn(
            'REACT_APP_API_BASE_URL is not set. Falling back to http://localhost:3005. ' +
                'Set REACT_APP_API_BASE_URL to your deployed backend URL.'
        );
    }

    return baseUrl.replace(/\/+$/, '');
};

/**
 * Turn stored media paths (/uploads/...) into absolute API URLs for <img src>.
 * Full http(s) URLs and blob/data previews are returned unchanged.
 */
export const resolveMediaUrl = (url) => {
    if (!url || typeof url !== 'string') return null;
    const trimmed = url.trim();
    if (!trimmed) return null;
    if (/^(https?:|blob:|data:)/i.test(trimmed)) return trimmed;
    const base = getApiBaseUrl();
    return `${base}${trimmed.startsWith('/') ? trimmed : `/${trimmed}`}`;
};

export const getDecodedToken = () => {
    const token = getToken();
    if (!token) return null;
    try {
        return jwtDecode(token);
    } catch (error) {
        console.error("Token decoding failed:", error);
        return null;
    }
};

/**
 * apiFetch handles authenticated requests.
 * It automatically throws errors for non-2xx responses.
 */
export const apiFetch = async (endpoint, options = {}) => {
    const token = getToken();
    const BASE_URL = getApiBaseUrl();
    const requestId = `web_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

    // Normalize endpoint so callers can pass:
    // - "/users/me" (recommended)
    // - "/api/users/me" or "api/users/me" (legacy)
    // - a full URL (http/https)
    const isFullUrl = /^https?:\/\//i.test(endpoint);

    let requestUrl;
    if (isFullUrl) {
        requestUrl = endpoint;
    } else {
        let path = String(endpoint || '').trim();

        // If someone accidentally passed BASE_URL (or BASE_URL/api) in the endpoint, strip it.
        // This keeps apiFetch resilient to mixed call-site conventions.
        if (path.toLowerCase().startsWith(BASE_URL.toLowerCase())) {
            path = path.slice(BASE_URL.length);
        }

        // Remove any leading "/api" or "api" segment to prevent double-prefixing.
        // Examples:
        //   "/api/academic-years" -> "/academic-years"
        //   "api/academic-years"  -> "/academic-years"
        path = path.replace(/^\s*\/?api(\/|$)/i, '/');

        if (!path.startsWith('/')) path = '/' + path;
        requestUrl = `${BASE_URL}/api${path}`;
    }

    const headers = {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        'x-request-id': requestId,
        ...options.headers,
    };

    if (options.body instanceof FormData) {
        delete headers['Content-Type'];
    }

    try {
        const response = await fetch(requestUrl, {
            ...options,
            headers,
        });
        const responseRequestId = response.headers.get('x-request-id');
        if (responseRequestId) {
            // Keep this minimal and optional: aids API<->frontend trace correlation in debugging.
            // eslint-disable-next-line no-console
            console.debug('[apiFetch] request-id', responseRequestId, requestUrl);
        }

        if (response.status === 401) {
            // Only redirect if not already on login page to avoid loops
            if (!window.location.pathname.includes('/login')) {
                removeToken();
                window.location.href = '/login';
            }
            throw new Error('Session expired. Please login again.');
        }

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            // Backend returns { message: "..." } usually
            const message = errorData.message || errorData.error || `Error ${response.status}: ${response.statusText}`;
            const err = new Error(message);
            err.data = errorData.data;
            err.status = response.status;
            throw err;
        }

        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * Consume a fetch Response body as JSON (call after apiFetch returns success).
 * Avoids silent failures and misleading "network" labels when the body is not valid JSON.
 */
export async function readResponseJson(response) {
    const text = await response.text();
    if (!text) return {};
    try {
        return JSON.parse(text);
    } catch {
        throw new Error('Invalid response from server (could not parse JSON).');
    }
}